<template>
  <RsModal
    size="xl"
    :value="value"
    :title="$ts('modalTagTranslations.title')"
    :button-text="$ts('modalTagTranslations.saveButton')"
    @input="$emit('input', false)"
    @submit="handleModalSubmit"
  >
    <RsInfoBlock
      :msg="$ts('modalTagTranslations.infoMsg')"
      class="mb-3"
    />
    <div class="rs-modal-tag-translations mx-n4 mb-n4">
      <RsTableTranslations
        ref="tagTranslationsTable"
        :items="translationsToEdit"
        :is-loading="isLoading"
        translate-property="name"
        class="px-1"
        :translate-property-display-name="$ts('modalTagTranslations.propertyDisplayName')"
        @on-save="handleSave"
      />
    </div>
  </RsModal>
</template>

<script lang="ts">
import { ITags } from '@llws/lift-entity-interfaces';
import { defineComponent, PropType } from 'vue';
import { IObjectWithTranslations } from '@/shared';
import { tags } from '@/store/typed/typed-modules';
import { getTranslationsByModelAndForeignIds } from '@/shared/services/translations';
import { getNewTranslation } from '@/shared/factory/new-translation';
import RsTableTranslations from '@/components/_global/RsTableTranslations.vue';
import { assertRef } from '@/shared/helpers/assert-ref';

export default defineComponent({
  name: 'RsModalTagTranslations',

  props: {
    value: {
      type: Boolean,
      default: false,
    },

    tags: {
      type: Array as PropType<ITags[]>,
      default: () => [],
    },
  },

  data() {
    return {
      isLoading: true,
      translationsToEdit: [] as IObjectWithTranslations<ITags>[],
    };
  },

  watch: {
    value() {
      this.getTranslations();
    },
  },

  methods: {
    async getTranslations(showLoading: boolean = true): Promise<void> {
      if (!this.tags.length || !this.value) {
        return;
      }

      this.isLoading = showLoading;

      const tagsIds = this.tags.map((tag) => tag.id!);
      const translations = await getTranslationsByModelAndForeignIds(tagsIds, 'tags');

      this.translationsToEdit = this.tags.map((tag) => ({
        ...tag,
        languages: (this.$rsGlobals.$siteSourceClientLanguages ?? []).map((l) => {
          const existing = translations.filter((tr) => `${tr.foreignId}` === `${tag.id}` && tr.languageId === l.id);

          return {
            ...l,
            translations: existing.length
              ? existing
              : [ getNewTranslation(tag.clientId, tag.id!, l.id!, 'name', 'tags') ],
          };
        }),
      }));

      this.isLoading = false;
    },

    async handleSave(data: IObjectWithTranslations<ITags>[]): Promise<void> {
      this.$eventBus.$emit('show-progress-toast');
      await tags.actions.saveTranslations(data);
      await this.getTranslations(false);
      this.$eventBus.$emit('show-success-toast', { message: this.$ts('modalTagTranslations.successToast') });
      this.$emit('input', false);
    },

    handleModalSubmit() {
      assertRef(this.$refs.tagTranslationsTable, RsTableTranslations)?.handleSave();
    },
  },
});
</script>

<style lang="scss">
  .rs-modal-tag-translations {
    height: 60vh;

    .rs-table-translations {
      border: 0;
      .rs-button {
        display: none;
      }

      .row.m-0.align-items-center.border-top {
        border-top: 0 !important;
      }
    }
  }
</style>
